import React from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import './LegalPages.scss';

const PrivacyPolicy: React.FC = () => {
  return (
    <>
      <Helmet>
        <title>Privacy Policy | App Developer Pro</title>
        <meta name="description" content="Learn how App Developer Pro collects, uses, and protects your personal information." />
        <link rel="canonical" href="https://appdeveloperpro.com/privacy" />
      </Helmet>

      <div className="privacy-page">
        {/* Hero Section */}
        <section className="legal-hero">
          <div className="container">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h1>Privacy Policy</h1>
              <p>Last updated: January 2025</p>
            </motion.div>
          </div>
        </section>

        {/* Content Section */}
        <motion.section 
          className="legal-content"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <div className="container">
            <article className="legal-article">
              <h2>Introduction</h2>
              <p>
                App Developer Pro ("we," "us," or "our") respects your privacy and is committed to 
                protecting your personal data. This privacy policy will inform you about how we look 
                after your personal data when you visit our website and tell you about your privacy 
                rights and how the law protects you.
              </p>

              <h2>Information We Collect</h2>
              <p>We may collect, use, store and transfer different kinds of personal data about you:</p>
              <ul>
                <li><strong>Identity Data:</strong> includes first name, last name, username, or similar identifier.</li>
                <li><strong>Contact Data:</strong> includes email address, telephone numbers, and mailing address.</li>
                <li><strong>Technical Data:</strong> includes internet protocol (IP) address, browser type and version, time zone setting, browser plug-in types and versions, operating system and platform.</li>
                <li><strong>Usage Data:</strong> includes information about how you use our website and services.</li>
                <li><strong>Marketing and Communications Data:</strong> includes your preferences in receiving marketing from us.</li>
              </ul>

              <h2>How We Use Your Information</h2>
              <p>We use your personal data for the following purposes:</p>
              <ul>
                <li>To provide and maintain our services</li>
                <li>To notify you about changes to our services</li>
                <li>To provide customer support</li>
                <li>To gather analysis or valuable information to improve our services</li>
                <li>To monitor the usage of our services</li>
                <li>To detect, prevent and address technical issues</li>
                <li>To provide you with news, special offers and general information</li>
              </ul>

              <h2>Data Security</h2>
              <p>
                We have implemented appropriate security measures to prevent your personal data from 
                being accidentally lost, used, or accessed in an unauthorized way, altered, or disclosed. 
                We limit access to your personal data to employees, agents, contractors and other third 
                parties who have a business need to know.
              </p>

              <h2>Data Retention</h2>
              <p>
                We will only retain your personal data for as long as necessary to fulfill the purposes 
                we collected it for, including for the purposes of satisfying any legal, accounting, 
                or reporting requirements.
              </p>

              <h2>Your Legal Rights</h2>
              <p>Under certain circumstances, you have rights under data protection laws in relation to your personal data, including:</p>
              <ul>
                <li>Request access to your personal data</li>
                <li>Request correction of your personal data</li>
                <li>Request erasure of your personal data</li>
                <li>Object to processing of your personal data</li>
                <li>Request restriction of processing your personal data</li>
                <li>Request transfer of your personal data</li>
                <li>Right to withdraw consent</li>
              </ul>

              <h2>Cookies</h2>
              <p>
                We use cookies and similar tracking technologies to track the activity on our website 
                and hold certain information. Cookies are files with a small amount of data which may 
                include an anonymous unique identifier. You can instruct your browser to refuse all 
                cookies or to indicate when a cookie is being sent.
              </p>

              <h2>Third-Party Links</h2>
              <p>
                Our website may include links to third-party websites, plug-ins, and applications. 
                Clicking on those links or enabling those connections may allow third parties to 
                collect or share data about you. We do not control these third-party websites and 
                are not responsible for their privacy statements.
              </p>

              <h2>Changes to This Privacy Policy</h2>
              <p>
                We may update our Privacy Policy from time to time. We will notify you of any changes 
                by posting the new Privacy Policy on this page and updating the "Last updated" date 
                at the top of this Privacy Policy.
              </p>

              <h2>Contact Us</h2>
              <p>
                If you have any questions about this Privacy Policy, please contact us:
              </p>
              <ul>
                <li>Email: info@appdeveloperpro.com</li>
                <li>Phone: +91 9999999999</li>
                <li>Address: 1st floor, Defence Enclave, Old Commercial Complex Sector 44, Sadarpur, Noida UP - 201303</li>
              </ul>
            </article>
          </div>
        </motion.section>
      </div>

      <style>{`
        .privacy-page {
          min-height: 100vh;
        }

        .legal-hero {
          background: linear-gradient(135deg, #0EA5E9 0%, #1E3A5F 100%);
          padding: 120px 0 80px;
          text-align: center;
          color: white;
        }

        .legal-hero h1 {
          font-size: 3rem;
          font-weight: 700;
          margin-bottom: 1rem;
        }

        .legal-hero p {
          opacity: 0.9;
        }

        .container {
          max-width: 900px;
          margin: 0 auto;
          padding: 0 20px;
        }

        .legal-content {
          padding: 80px 0;
          background: #fff;
        }

        .legal-article {
          font-size: 1.1rem;
          line-height: 1.8;
          color: #333;
        }

        .legal-article h2 {
          font-size: 1.5rem;
          font-weight: 700;
          color: #1a1a2e;
          margin: 40px 0 20px;
        }

        .legal-article h2:first-of-type {
          margin-top: 0;
        }

        .legal-article p {
          margin-bottom: 20px;
        }

        .legal-article ul {
          margin: 20px 0;
          padding-left: 30px;
        }

        .legal-article li {
          margin-bottom: 10px;
        }

        .legal-article strong {
          color: #1a1a2e;
        }

        @media (max-width: 768px) {
          .legal-hero h1 {
            font-size: 2rem;
          }

          .legal-article {
            font-size: 1rem;
          }
        }
      `}</style>
    </>
  );
};

export default PrivacyPolicy;

